import { useMutation } from '@tanstack/react-query'
import { IHttpError, IResponse, Model, ObjectsFactory } from './core'
import { IFile } from './file'
import { IBank } from './bank'

export enum BalanceTransactionStatusEnum {
  Pending = 'pending',
  Approved = 'approved',
  Rejected = 'rejected'
}

export const getStatusConfig = (status: string) => {
  switch (status.toLowerCase()) {
    case 'pending':
      return {
        backgroundColor: '#FF9F43',
        textColor: '#FFFFFF'
      }
    case 'approved':
      return {
        backgroundColor: '#49C76E',
        textColor: '#FFFFFF'
      }
    case 'rejected':
      return {
        backgroundColor: '#F24C52',
        textColor: '#FFFFFF'
      }
    case 'deleted':
      return {
        backgroundColor: '#F24C52',
        textColor: '#FFFFFF'
      }
    default:
      return {
        backgroundColor: '#F5F5F5',
        textColor: '#757575'
      }
  }
}

export interface IBalanceTransactionResponse {
  message: string
  data: IBalanceTransactionRecord[]
  meta: IMetaBalanceTransacTion
}

export interface IBalanceTransactionRecord {
  id: number
  created_at: string
  updated_at: string
  code: string
  month: string
  company_id: number
  voucher: string
  voucher_tax: string
  debt_disbursement: string
  different: string
  file_id: string
  status: string
  note?: string
  extra: any
  approver: number
  approve_at: string
  company: ICompanyBalance
  created_user: any
  updated_user: any
  approverUser: any
  file: IFile
}

export interface ICompanyBalance {
  id: number
  created_at: string
  updated_at: string
  created_by: number
  updated_by: number
  name: string
  system_code: string
  company_code: string
  business_number: string
  address: string
  phone: any
  email: string
  bank_id: number
  bank_account_number: string
  establish_date: string
  company_type: string
  poc_firstname: string
  poc_phone: string
  poc_phone_code: string
  total_user: number
  working_rate: string
  working_type: string
  service_fee: string
  vat_rate: string
  eligibility_rate: number
  setting_updated_at: string
  setting_updated_by: string
  logo_id: string
  closing_date: number
  minimum_working_days: number
  memo: string
  closing_date_type: string
  status: string
  logo_file: any
  bank: IBank
  current_pay_period: string
}

export function useTransactionStatusOptions() {
  const { __ } = useTranslator()

  return [
    { value: BalanceTransactionStatusEnum.Pending, label: __('Pending') },
    { value: BalanceTransactionStatusEnum.Approved, label: __('Approved') },
    { value: BalanceTransactionStatusEnum.Rejected, label: __('Rejected') }
  ]
}

export interface IMetaBalanceTransacTion {
  total: number
  perPage: number
}

const modelConfig = {
  path: 'balance-transaction'
}

export class BalanceTransaction extends Model {
  static queryKeys = {
    paginate: 'BALANCE_TRANSACTION_PAGINATE_QUERY',
    findOne: 'BALANCE_TRANSACTION_FIND_ONE_QUERY',
    getBalanceTransaction: 'BALANCE_TRANSACTION_GET_BALANCE_TRANSACTION_QUERY',
    getBalanceTransactionDetail: 'BALANCE_TRANSACTION_GET_BALANCE_TRANSACTION_QUERY_DETAIL'
  }

  static objects = ObjectsFactory.factory<any>(modelConfig, this.queryKeys)

  static getBalanceTransactionById(id: number) {
    return {
      queryKey: [this.queryKeys.getBalanceTransactionDetail, id],
      queryFn: () => {
        return this.api
          .get<IBalanceTransactionRecord>({
            url: `/balance-transaction/${id}`
          })
          .then(r => r.data)
      }
    }
  }
  static createBalanceTransaction(data: any) {
    return this.api.post<IResponse<any>>({
      url: `/balance-transaction`,
      data
    })
  }

  static approveBalanceTransaction(id: number) {
    return this.api.post<IResponse<IBalanceTransactionRecord>>({
      url: `/balance-transaction/${id}/approve`
    })
  }
  static deleteBalanceTransaction(id: number) {
    return this.api.delete<IResponse<IBalanceTransactionRecord>>({
      url: `/balance-transaction/${id}`
    })
  }
  static updateBalanceTransactionById(id: number, payload: IBalanceTransactionRecord) {
    return this.api.put<IResponse<IBalanceTransactionRecord>>({
      url: `/balance-transaction/${id}`,
      data: payload
    })
  }
  static rejectBalanceTransaction(id: number, note: string) {
    return this.api.post<IResponse<IBalanceTransactionRecord>>({
      url: `/balance-transaction/${id}/reject`,
      data: { note }
    })
  }
}

BalanceTransaction.setup(modelConfig)

export function useBalanceTransactionCreateMutation() {
  return useMutation<IResponse<any>, IHttpError, { data: any }>({
    mutationFn: ({ data }) => {
      return BalanceTransaction.createBalanceTransaction(data).then(r => r.data)
    }
  })
}

export function useBalanceTransactionApproveMutation() {
  return useMutation<IResponse<IBalanceTransactionRecord>, IHttpError, { id: number }>({
    mutationFn: ({ id }) => {
      return BalanceTransaction.approveBalanceTransaction(id).then(r => r.data)
    }
  })
}

export function useBalanceTransactionDeleteMutation() {
  return useMutation<IResponse<IBalanceTransactionRecord>, IHttpError, { id: number }>({
    mutationFn: ({ id }) => {
      return BalanceTransaction.deleteBalanceTransaction(id).then(r => r.data)
    }
  })
}

export function useBalanceTransactionUpdateMutation() {
  return useMutation<IResponse<IBalanceTransactionRecord>, IHttpError, { id: number; data: any }>({
    mutationFn: ({ id, data }) => {
      return BalanceTransaction.updateBalanceTransactionById(id, data).then(r => r.data)
    }
  })
}

export function useBalanceTransactionRejectMutation() {
  return useMutation<IResponse<IBalanceTransactionRecord>, IHttpError, { id: number; note: string }>({
    mutationFn: ({ id, note }) => {
      return BalanceTransaction.rejectBalanceTransaction(id, note).then(r => r.data)
    }
  })
}
